/**
 * HERO "Terminbuchung" module — 1:1 port of the prototype `showTermin` blocks
 * (sidebar ~L569, booking-page mock content ~L1408) of
 * `website redesign (delete later)/project/Hausverwaltung Hero.dc.html`.
 *
 * Layout: a 220px (SIDEBAR_WIDTH) sidebar + a full-bleed #FAFAF8 content area
 * holding a centered 660×440 booking-page card. Verbatim German copy, exact
 * px/colors, lucide-react icons. The booking mini-calendar uses the same
 * June 2026 month build / availability rules as `kalData` in the prototype.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Clock,
  Globe,
  List,
  MapPin,
  Phone,
  Repeat,
  UserPlus,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** A single booking-calendar day cell. */
type BookCell = {
  readonly day: string;
  readonly bg: string;
  readonly color: string;
  readonly weight: number;
  readonly cursor: "pointer" | "default";
};

/** accentSoft used by the booking cells (prototype hexToRgba(accent, 0.09)). */
const BOOK_AVAIL_BG = "rgba(0,128,112,0.09)";

/**
 * Build the June 2026 booking weeks exactly like the prototype `kalData`:
 * month starts Monday, available = weekday (col < 5) && day >= 22, day 27 = Sa
 * is the selected (accent-filled) cell.
 */
function buildBookWeeks(): readonly (readonly BookCell[])[] {
  // June 2026: 1st is a Monday → startDow = 1; 30 days; prev month (May) = 31.
  const startDow = 1;
  const dim = 30;
  const prevDim = 31;
  const cells: { day: number; inMonth: boolean; col: number }[] = [];
  for (let i = 0; i < 42; i++) {
    const dn = i - startDow + 1;
    let day: number;
    let inMonth: boolean;
    if (dn < 1) {
      day = prevDim + dn;
      inMonth = false;
    } else if (dn > dim) {
      day = dn - dim;
      inMonth = false;
    } else {
      day = dn;
      inMonth = true;
    }
    cells.push({ day, inMonth, col: i % 7 });
  }
  const bookCells: BookCell[] = cells.map((c) => {
    const avail = c.inMonth && c.col < 5 && c.day >= 22;
    const selected = c.inMonth && c.day === 27;
    return {
      day: c.inMonth ? String(c.day) : "",
      bg: selected ? H.accent : avail ? BOOK_AVAIL_BG : "transparent",
      color: selected ? ld("#ffffff", "#181b1f") : avail ? H.accent : ld("rgba(11,15,25,0.26)", "rgba(250,250,250,0.26)"),
      weight: selected ? 700 : avail ? 600 : 400,
      cursor: avail || selected ? "pointer" : "default",
    };
  });
  const weeks: BookCell[][] = [];
  for (let i = 0; i < bookCells.length; i += 7) {
    weeks.push(bookCells.slice(i, i + 7));
  }
  return weeks.slice(0, 5);
}

const BOOK_WEEKS = buildBookWeeks();

/** A single event-type row in the sidebar's "Meine Ereignistypen" list. */
type EventType = {
  readonly icon: ReactNode;
  readonly labelKey: string;
  readonly durationKey: string;
  readonly active?: boolean;
};

const EVENT_TYPES: readonly EventType[] = [
  {
    icon: <CalendarClock width={14} height={14} strokeWidth={1.9} />,
    labelKey: "wohnungsuebergabe",
    durationKey: "k45Min",
    active: true,
  },
  {
    icon: <Users width={14} height={14} strokeWidth={1.8} />,
    labelKey: "sprechstundeEigentuemer",
    durationKey: "k30Min",
  },
  {
    icon: <Repeat width={14} height={14} strokeWidth={1.8} />,
    labelKey: "verwalterwechsel",
    durationKey: "k60Min",
  },
  {
    icon: <UserPlus width={14} height={14} strokeWidth={1.8} />,
    labelKey: "eigentuemerOnboarding",
    durationKey: "k45Min",
  },
  {
    icon: <Phone width={14} height={14} strokeWidth={1.8} />,
    labelKey: "telefonat",
    durationKey: "k15Min",
  },
];

/** A single event-type list row (26×26 icon tile + label + duration). */
function EventTypeRow(props: { readonly item: EventType }): ReactNode {
  const t = useTranslations("marketing.moduleTerminbuchung");
  const { item } = props;
  const active = item.active === true;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 8px",
        borderRadius: 8,
        background: active ? H.accentSoft : "transparent",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          background: active ? ld("#fff", "#181b1f") : ld("#F1F2F4", "#2b2e32"),
          border: active ? `1px solid ${H.accentBorder}` : undefined,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: active ? H.accent : ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
          flexShrink: 0,
        }}
      >
        {item.icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: H.sans,
            fontSize: 13,
            fontWeight: active ? 600 : 500,
            color: active ? H.accent : H.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t(item.labelKey)}
        </div>
        <div style={{ fontFamily: H.sans, fontSize: 11, color: H.inkDim }}>
          {t(item.durationKey)}
        </div>
      </div>
    </div>
  );
}

/** Sidebar: "Kalender" back-link, "Terminbuchung" title, two nav rows, list. */
function TerminbuchungSidebar(props: {
  readonly setActive: (key: ModuleProps["active"]) => void;
}): ReactNode {
  const t = useTranslations("marketing.moduleTerminbuchung");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={() => props.setActive("kalender")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontFamily: H.sans,
          fontSize: 12.5,
          color: H.inkFaint,
          cursor: "pointer",
          padding: "0 2px 15px",
        }}
      >
        <ChevronLeft width={14} height={14} strokeWidth={1.9} />
        {t("kalender")}
      </div>
      <div
        style={{
          fontFamily: H.sans,
          fontSize: 16,
          fontWeight: 700,
          color: H.ink,
          padding: "0 2px 16px",
        }}
      >
        {t("terminbuchung")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 9px",
            borderRadius: 8,
            fontFamily: H.sans,
            fontSize: 13.5,
            fontWeight: 600,
            color: H.accent,
            background: H.accentSoft,
          }}
        >
          <CalendarClock width={16} height={16} strokeWidth={1.9} />
          {t("ereignistypen")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 9px",
            borderRadius: 8,
            fontFamily: H.sans,
            fontSize: 13.5,
            color: H.inkSoft,
          }}
        >
          <span style={{ color: H.inkDim, display: "inline-flex" }}>
            <List width={16} height={16} strokeWidth={1.8} />
          </span>
          {t("buchungen")}
        </div>
      </div>
      <div
        style={{
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          color: H.inkGhost,
          textTransform: "uppercase",
          padding: "18px 6px 9px",
        }}
      >
        {t("meineEreignistypen")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {EVENT_TYPES.map((item) => (
          <EventTypeRow key={item.labelKey} item={item} />
        ))}
      </div>
    </div>
  );
}

/** Left column of the booking card: logo, event title, meta, description. */
function BookingDetails(): ReactNode {
  const t = useTranslations("marketing.moduleTerminbuchung");
  return (
    <div
      style={{
        width: "44%",
        flexShrink: 0,
        padding: 24,
        borderRight: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span
        style={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: H.ink,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <svg
          width="17"
          height="14"
          style={{ display: "block", overflow: "visible" }}
        >
          <path
            d="M8.5 2 L15 12 L2 12 Z"
            style={{
              fill: ld("#fff", "#181b1f"),
              stroke: ld("#fff", "#181b1f"),
              strokeWidth: 3.2,
              strokeLinejoin: "round",
            }}
          />
        </svg>
      </span>
      <div
        style={{
          fontFamily: H.sans,
          fontSize: 12.5,
          fontWeight: 600,
          color: H.inkFaint,
        }}
      >
        {t("hausverwaltungMusterstadt")}
      </div>
      <div
        style={{
          fontFamily: H.sans,
          fontSize: 21,
          fontWeight: 700,
          color: H.ink,
          marginTop: 3,
        }}
      >
        {t("wohnungsuebergabe")}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 9,
          marginTop: 17,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            fontFamily: H.sans,
            fontSize: 13.5,
            color: ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)"),
          }}
        >
          <span style={{ color: H.inkDim, display: "inline-flex" }}>
            <Clock width={16} height={16} strokeWidth={1.8} />
          </span>
          {t("k45Min")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            fontFamily: H.sans,
            fontSize: 13.5,
            color: ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)"),
          }}
        >
          <span style={{ color: H.inkDim, display: "inline-flex" }}>
            <MapPin width={16} height={16} strokeWidth={1.8} />
          </span>
          {t("ortWohneinheit")}
        </div>
      </div>
      <div
        style={{
          fontFamily: H.sans,
          fontSize: 13,
          lineHeight: 1.62,
          color: H.inkMute,
          marginTop: 16,
        }}
      >
        {t("uebergabeterminAuszugWaehlenFestes")}
      </div>
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          gap: 14,
          fontFamily: H.sans,
          fontSize: 11.5,
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          paddingTop: 16,
        }}
      >
        <span style={{ cursor: "pointer" }}>{t("datenschutz")}</span>
        <span style={{ cursor: "pointer" }}>{t("impressum")}</span>
      </div>
    </div>
  );
}

/** Right column of the booking card: date/time picker + mini calendar. */
function BookingPicker(): ReactNode {
  const t = useTranslations("marketing.moduleTerminbuchung");
  return (
    <div
      style={{
        flex: 1,
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontFamily: H.sans,
          fontSize: 15,
          fontWeight: 700,
          color: H.ink,
          marginBottom: 14,
        }}
      >
        {t("datumUhrzeitWaehlen")}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 20,
          marginBottom: 11,
        }}
      >
        <span
          style={{
            color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
            display: "inline-flex",
            cursor: "pointer",
          }}
        >
          <ChevronLeft width={17} height={17} strokeWidth={1.9} />
        </span>
        <span
          style={{
            fontFamily: H.sans,
            fontSize: 14,
            fontWeight: 700,
            color: H.ink,
          }}
        >
          {t("juni2026")}
        </span>
        <span
          style={{ color: H.accent, display: "inline-flex", cursor: "pointer" }}
        >
          <ChevronRight width={17} height={17} strokeWidth={1.9} />
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7,1fr)",
          textAlign: "center",
          fontFamily: H.sans,
          fontSize: 11,
          fontWeight: 600,
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          paddingBottom: 5,
        }}
      >
        <span>{t("mo")}</span>
        <span>{t("di")}</span>
        <span>{t("mi")}</span>
        <span>{t("do")}</span>
        <span>{t("fr")}</span>
        <span>{t("sa")}</span>
        <span>{t("so")}</span>
      </div>
      {BOOK_WEEKS.map((week, wi) => (
        <div
          key={wi}
          style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)" }}
        >
          {week.map((cell, di) => (
            <span
              key={di}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 33,
                margin: 1,
                borderRadius: "50%",
                fontFamily: H.sans,
                fontSize: 13,
                background: cell.bg,
                color: cell.color,
                fontWeight: cell.weight,
                cursor: cell.cursor,
              }}
            >
              {cell.day}
            </span>
          ))}
        </div>
      ))}
      <div style={{ marginTop: "auto", paddingTop: 14 }}>
        <div
          style={{
            fontFamily: H.sans,
            fontSize: 12,
            fontWeight: 600,
            color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
            marginBottom: 6,
          }}
        >
          {t("zeitzone")}
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontFamily: H.sans,
            fontSize: 13,
            color: ld("rgba(11,15,25,0.7)", "rgba(250,250,250,0.7)"),
          }}
        >
          <Globe width={15} height={15} strokeWidth={1.8} />
          {t("mitteleuropaeischeZeit")}
        </div>
      </div>
    </div>
  );
}

/**
 * HERO "Terminbuchung" module — Ereignistypen sidebar + a centered booking-page
 * mock (event details left, June 2026 date picker right).
 */
export function ModuleTerminbuchung(props: ModuleProps): ReactNode {
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <TerminbuchungSidebar setActive={props.setActive} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: MODULE_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: ld("#FAFAF8", "#1e2125"),
          padding: 22,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            width: 660,
            maxWidth: "100%",
            height: 440,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: `0 18px 50px -28px ${ld("rgba(11,15,25,0.28)", "rgba(0,0,0,0.47)")}`,
          }}
        >
          <BookingDetails />
          <BookingPicker />
        </div>
      </div>
    </div>
  );
}
